import { composeWrappers } from "@redotech/react-util/component";
import { OutboundViewsProvider } from "@redotech/redo-merchant-fulfillment-app/outbound-views";
import { AssignedFulfillmentServicesService } from "@redotech/redo-merchant-fulfillment-app/services/assigned-fulfillment-service";
import { LocationService } from "@redotech/redo-merchant-fulfillment-app/services/location-service";
import { ParcelService } from "@redotech/redo-merchant-fulfillment-app/services/parcel-service";
import { ConsolidationViewsProvider } from "@redotech/redo-merchant-fulfillment-app/wms/consolidation/consolidation-views";
import { WorkOrderViewsProvider } from "@redotech/redo-merchant-fulfillment-app/work-orders/work-order-views";
import { InventoryViewsProvider } from "@redotech/redo-merchant-inventory-app/inventory-views";
import { OrderTrackingOrderViewsProvider } from "@redotech/redo-merchant-marketing-app/order-tracking/orders/order-tracking-order-views";
import { OrderTrackingShipmentViewsProvider } from "@redotech/redo-merchant-marketing-app/order-tracking/shipments/order-tracking-shipment-views";
import { EmailIntegrationsService } from "@redotech/redo-merchant-support-app/services/email-integrations";
import { PhoneNumberService } from "@redotech/redo-merchant-support-app/services/phone-number-service";
import { VoiceDeviceStateContextProvider } from "@redotech/redo-merchant-support-app/services/voice-device-state";
import { SupportViewsFoldersProvider } from "@redotech/redo-merchant-support-app/v2/providers/support-views-folders-provider";
import { memo, ReactElement } from "react";
import { FeedbackPopoverProvider } from "../app/navigation/top-navigation/feedback-popover-context";
import { CatalogNavAnnouncement } from "../catalog/catalog-nav-announcement";
import { FeatureOnboarding } from "../feature-onboarding";
import { CreditIncentiveHost } from "../feature-onboarding/credit-incentive/credit-incentive-host";
import { SettingsMovingModalGate } from "../feature-onboarding/settings-moving-modal-gate";
import { WithdrawalsOnboardingTriggerProvider } from "../feature-onboarding/withdrawals-onboarding-trigger";
import { ProductSuiteProvider } from "../redo-suite/product-suite";
import { ReturnsNavRearrangementPopup } from "../return/returns-nav-rearrangement-popup";
import { ClaimViewsProvider } from "./claims/claim-views";
import { ClaimViewOrderingService } from "./claims/claims-view-ordering-service";
import { TeamSwitcherService } from "./global/team-switcher-service";
import { UserProfileService } from "./global/user-profile-service";
import { DiscountsService } from "./marketing/discounts-service";
import { ReviewsToReviewService } from "./marketing/reviews-to-review-service";
import { SegmentsService } from "./marketing/segments-service";
import { OrdersReviewCountService } from "./orders/orders-review-count-service";
import { RecycleViewOrderingService } from "./recycle/recycle-view-ordering-service";
import { RecycleViewsProvider } from "./recycle/recycle-views";
import { ReturnViewsProvider } from "./returns/return-views";
import { ReturnViewOrderingService } from "./returns/returns-view-ordering-service";
import { WarrantyViewOrderingService } from "./warranties/warranty-view-ordering-service";
import { WarrantyViewsProvider } from "./warranties/warranty-views";

export const DataServices = memo(function DataServices({
  children,
}: {
  children: ReactElement;
}) {
  return composeWrappers(
    (inner) => (
      <SupportViewsFoldersProvider>{inner}</SupportViewsFoldersProvider>
    ),
    (inner) => <ReturnViewsProvider>{inner}</ReturnViewsProvider>,
    (inner) => <ClaimViewsProvider>{inner}</ClaimViewsProvider>,
    (inner) => <WarrantyViewsProvider>{inner}</WarrantyViewsProvider>,
    (inner) => <RecycleViewsProvider>{inner}</RecycleViewsProvider>,
    (inner) => <OutboundViewsProvider>{inner}</OutboundViewsProvider>,
    (inner) => <WorkOrderViewsProvider>{inner}</WorkOrderViewsProvider>,
    (inner) => <ConsolidationViewsProvider>{inner}</ConsolidationViewsProvider>,
    (inner) => <InventoryViewsProvider>{inner}</InventoryViewsProvider>,
    (inner) => (
      <OrderTrackingShipmentViewsProvider>
        {inner}
      </OrderTrackingShipmentViewsProvider>
    ),
    (inner) => (
      <OrderTrackingOrderViewsProvider>{inner}</OrderTrackingOrderViewsProvider>
    ),
    (inner) => <PhoneNumberService>{inner}</PhoneNumberService>,
    (inner) => (
      <VoiceDeviceStateContextProvider>{inner}</VoiceDeviceStateContextProvider>
    ),
    (inner) => <EmailIntegrationsService>{inner}</EmailIntegrationsService>,
    (inner) => <ParcelService>{inner}</ParcelService>,
    (inner) => <LocationService>{inner}</LocationService>,
    (inner) => (
      <AssignedFulfillmentServicesService>
        {inner}
      </AssignedFulfillmentServicesService>
    ),
    (inner) => <ProductSuiteProvider>{inner}</ProductSuiteProvider>,
    (inner) => <SegmentsService>{inner}</SegmentsService>,
    (inner) => <DiscountsService>{inner}</DiscountsService>,
    (inner) => <ReturnViewOrderingService>{inner}</ReturnViewOrderingService>,
    (inner) => <ClaimViewOrderingService>{inner}</ClaimViewOrderingService>,
    (inner) => (
      <WarrantyViewOrderingService>{inner}</WarrantyViewOrderingService>
    ),
    (inner) => <RecycleViewOrderingService>{inner}</RecycleViewOrderingService>,
    (inner) => <OrdersReviewCountService>{inner}</OrdersReviewCountService>,
    (inner) => (
      <WithdrawalsOnboardingTriggerProvider>
        {inner}
      </WithdrawalsOnboardingTriggerProvider>
    ),
    // Above UserProfileService so its "Give feedback" menu item can open it.
    (inner) => <FeedbackPopoverProvider>{inner}</FeedbackPopoverProvider>,
    (inner) => <UserProfileService>{inner}</UserProfileService>,
    (inner) => <TeamSwitcherService>{inner}</TeamSwitcherService>,
    (inner) => <ReviewsToReviewService>{inner}</ReviewsToReviewService>,
    (inner) => (
      <>
        <FeatureOnboarding />
        <CreditIncentiveHost />
        <SettingsMovingModalGate />
        <ReturnsNavRearrangementPopup />
        <CatalogNavAnnouncement />
        {inner}
      </>
    ),
  )(children);
});
